import React, { useEffect, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { ViewportDrawer } from '../Overlay';
import { SetAvatar, SetCheck, SetInput, SetSelect, SetSwitch, SetTextarea } from '../SettingsShell/controls';
import { ROLES } from '../../settings-practice/types';
import type { Provider } from '../../settings-practice/types';

export interface ProviderDrawerProps {
  provider: Provider | null;
  providers: Provider[];
  location: string;
  onClose: () => void;
  /** Persist the edited provider; the drawer stays open if it throws. */
  onSave?: (next: Provider) => void | Promise<void>;
}

/**
 * One provider (spec 15 §5.2). Explicit save.
 *
 * Hours come from the PMS and are rendered as text with their source, never as
 * editable inputs — the next sync would discard whatever was typed (§3.5).
 * Legal name, credentials, licence number, work email and work phone are
 * deliberately absent: ELVA has no use for them.
 */
export function ProviderDrawer({ provider, providers, location, onClose, onSave }: ProviderDrawerProps) {
  const [p, setP] = useState<Provider | null>(provider);
  useEffect(() => { setP(provider); }, [provider]);

  const set = <K extends keyof Provider>(k: K, v: Provider[K]) => {
    setP((prev) => (prev ? { ...prev, [k]: v } : prev));
  };

  const coverOptions = [
    { value: '', label: 'Nobody covering' },
    ...providers.filter((x) => x.id !== p?.id).map((x) => ({ value: x.id, label: x.elvaSays })),
  ];

  return (
    <ViewportDrawer open={provider !== null} onClose={onClose} width="min(720px, 96vw)">
      {p ? (
        <div className="flex h-full flex-col bg-[var(--surface-card)]">
          <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
            <div className="flex items-center gap-3">
              <SetAvatar initials={p.initials} colour={p.colour} size={44} />
              <div>
                <h2 className="m-0 text-[18px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">{p.name}</h2>
                <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-muted)]">
                  On the roster since {p.onRosterSince}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
            >
              <Icon kind="x" size={14} stroke={2} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* roles and name */}
            <Section title="Roles and name">
              <FieldLabel
                label="Roles"
                desc={
                  <>
                    <strong className="text-[var(--text-secondary)]">
                      Appointment reasons route to a role, not to a person
                    </strong>
                    , so this is what decides which reasons ELVA offers this provider for.{' '}
                    <strong className="text-[var(--text-secondary)]">Tick as many as apply</strong> — in a new practice
                    the owner is often the hygienist too. Untick one and every reason routing to it stops offering them,
                    the same day.
                  </>
                }
              />
              <div className="mt-2.5 flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <SetCheck
                    key={r.id}
                    checked={p.roles.includes(r.id)}
                    onChange={(next) => {
                      set('roles', next ? [...p.roles, r.id] : p.roles.filter((x) => x !== r.id));
                    }}
                  >
                    {r.label}
                  </SetCheck>
                ))}
              </div>

              <div className="mt-5">
                <FieldLabel
                  label="What ELVA calls them"
                  desc={
                    <>
                      <strong className="text-[var(--text-secondary)]">Said out loud on every call</strong>, and written
                      in every text and email. <em>&ldquo;{p.name}&rdquo;</em> is not how a receptionist introduces
                      somebody.
                    </>
                  }
                />
                <div className="mt-2 w-[260px]">
                  <SetInput ariaLabel="What ELVA calls them" value={p.elvaSays} onChange={(v) => { set('elvaSays', v); }} />
                </div>
              </div>

              <div className="mt-5">
                <FieldLabel
                  label="Bio"
                  desc={
                    <>
                      <strong className="text-[var(--text-secondary)]">One reader: the online booking page.</strong> ELVA
                      never says it on a call. Empty, the booking page shows the name and role alone.
                    </>
                  }
                />
                <div className="mt-2">
                  <SetTextarea ariaLabel="Bio" rows={3} value={p.bio} onChange={(v) => { set('bio', v); }} />
                </div>
              </div>
            </Section>

            {/* when they work — PMS-synced, read-only */}
            <Section
              title="When they work"
              desc={
                <>
                  From the practice management system.{' '}
                  <strong className="text-[var(--text-secondary)]">Practice hours cap it</strong> — a wider column is not
                  an error and is not deleted, it is simply not offered.
                </>
              }
            >
              {p.days.map((d, i) => (
                <div
                  key={d.label}
                  className="flex items-center justify-between gap-4 py-2.5"
                  style={i === p.days.length - 1 ? undefined : { borderBottom: '1px solid #edf0f6' }}
                >
                  <span
                    className={
                      d.working
                        ? 'text-[13.5px] font-semibold text-[var(--text-primary)]'
                        : 'text-[13.5px] font-semibold text-[var(--text-muted)]'
                    }
                  >
                    {d.label}
                  </span>
                  <span className="flex items-center gap-4">
                    {d.cappedNote ? (
                      <span className="text-[12px] text-[var(--warn-amber)]">{d.cappedNote}</span>
                    ) : null}
                    {d.working ? (
                      <span className="font-[var(--font-mono)] text-[13px] text-[var(--text-primary)]">
                        {d.from} — {d.to}
                      </span>
                    ) : (
                      <span className="text-[13px] italic text-[var(--text-muted)]">Day off</span>
                    )}
                  </span>
                </div>
              ))}
            </Section>

            {/* time off */}
            <Section
              title="Time off"
              desc={
                <>
                  ELVA offers nothing in this column on these dates.{' '}
                  <strong className="text-[var(--text-secondary)]">Covered by is the field that does the work</strong> —
                  without it ELVA can only say somebody is away; with it, it offers the cover instead of ending the
                  conversation.
                </>
              }
            >
              {p.timeOff.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center gap-3 py-2">
                  <SetInput ariaLabel="Time off from" className="w-[150px]" value={t.from} />
                  <span className="text-[12.5px] text-[var(--text-muted)]">to</span>
                  <SetInput ariaLabel="Time off to" className="w-[150px]" value={t.to} />
                  <span className="ml-auto flex items-center gap-2">
                    <SetSelect
                      ariaLabel="Covered by"
                      value={t.coveredBy ?? ''}
                      options={coverOptions}
                      className={t.coveredBy === null ? 'bg-[#FBF3E4] text-[var(--warn-amber)]' : ''}
                      onChange={(v) => {
                        set(
                          'timeOff',
                          p.timeOff.map((x) => (x.id === t.id ? { ...x, coveredBy: v === '' ? null : v } : x)),
                        );
                      }}
                    />
                    <button
                      type="button"
                      aria-label="Remove this time off"
                      onClick={() => {
                        set('timeOff', p.timeOff.filter((x) => x.id !== t.id));
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-[9px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
                    >
                      <Icon kind="trash" size={13} stroke={2} />
                    </button>
                  </span>
                </div>
              ))}
              <button
                type="button"
                className="mt-2 inline-flex items-center gap-1.5 rounded-[10px] border border-dashed border-[var(--border-default)] px-3 py-2 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--surface-sunken)]"
              >
                <Icon kind="plus" size={12} stroke={2.4} /> Add time off
              </button>
            </Section>

            {/* what ELVA may do */}
            <Section title="What ELVA may do">
              <SwitchRow
                label="Patients may book with them online"
                desc="Off means ELVA never offers this column to a patient. It does not hide them from the calendar — staff book them by hand."
                checked={p.onlineBooking}
                onChange={(v) => { set('onlineBooking', v); }}
              />
              <SwitchRow
                label="Accepting new patients"
                desc={
                  <>
                    <strong className="text-[var(--text-secondary)]">Separate from the row above.</strong> On means anyone
                    may book; off means{' '}
                    <strong className="text-[var(--text-secondary)]">existing patients only</strong>, and ELVA offers a
                    different provider to somebody who has never been in.
                  </>
                }
                checked={p.acceptingNewPatients}
                onChange={(v) => { set('acceptingNewPatients', v); }}
              />
              <SwitchRow
                label="May be double-booked for an emergency"
                desc={
                  <>
                    <strong className="text-[var(--text-secondary)]">
                      The practice-wide emergency setting allows it; a provider who has said no is switched off here.
                    </strong>{' '}
                    Once, as a setting — rather than as a sentence in house rules that fights another sentence.
                  </>
                }
                checked={p.mayBeDoubleBooked}
                onChange={(v) => { set('mayBeDoubleBooked', v); }}
              />
              <SwitchRow
                last
                label="Show in the calendar"
                desc={
                  <>
                    Off hides the column from staff as well.{' '}
                    <strong className="text-[var(--text-secondary)]">
                      For somebody who has left and is still in the PMS
                    </strong>{' '}
                    — use it rather than waiting for the sync.
                  </>
                }
                checked={p.showInCalendar}
                onChange={(v) => { set('showInCalendar', v); }}
              />
            </Section>

            {/* production */}
            <Section title="Production">
              <FieldLabel
                label="Monthly target"
                desc={
                  <>
                    Read by <strong className="text-[var(--text-secondary)]">Calendar insights</strong> for per-provider
                    attainment.{' '}
                    <strong className="text-[var(--text-secondary)]">
                      The five targets do not have to add up to the practice goal
                    </strong>{' '}
                    on General info, and the report does not assume they do.
                  </>
                }
              />
              <div className="mt-2 w-[180px]">
                <SetInput
                  ariaLabel="Monthly target"
                  prefix="$"
                  mono
                  value={p.monthlyTarget}
                  onChange={(v) => { set('monthlyTarget', v); }}
                />
              </div>
            </Section>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
            <span className="text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
              Saved to <strong className="text-[var(--text-secondary)]">{location.split(' (')[0]}</strong>. A provider
              who also works at another location is configured there separately.
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void Promise.resolve(onSave?.(p)).then(onClose, () => undefined);
                }}
                className="rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
              >
                Save
              </button>
            </span>
          </div>
        </div>
      ) : null}
    </ViewportDrawer>
  );
}

function Section({ title, desc, children }: { title: string; desc?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-b border-[var(--border-default)] px-6 py-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">{title}</div>
      {desc ? <p className="m-0 mt-1.5 text-[12.5px] leading-[1.6] text-[var(--text-muted)]">{desc}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function FieldLabel({ label, desc }: { label: string; desc?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[13px] font-bold text-[var(--text-primary)]">{label}</div>
      {desc ? <p className="m-0 mt-1 text-[12px] leading-[1.6] text-[var(--text-muted)]">{desc}</p> : null}
    </div>
  );
}

function SwitchRow({
  label,
  desc,
  checked,
  onChange,
  last,
}: {
  label: string;
  desc: React.ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  last?: boolean;
}) {
  return (
    <div
      className="flex items-start justify-between gap-6 py-3.5"
      style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-[var(--text-primary)]">{label}</div>
        <p className="m-0 mt-1 text-[12.5px] leading-[1.6] text-[var(--text-muted)]">{desc}</p>
      </div>
      <SetSwitch label={label} checked={checked} onChange={onChange} />
    </div>
  );
}
